import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MapPin } from 'lucide-react';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';
import { readShopOccupiedSpotIds } from '@/lib/location/api';
import { createLatestReadGate } from '@/lib/location/floorOccupancy';
import { useForegroundRecovery } from '@/hooks/useForegroundRecovery';

interface ParkingSpotPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string | null;
  shopId: string;
  /** Called after a successful move (with or without specific spot) */
  onComplete?: () => void;
  /** If true, skip the RPC call and just return the selected spot id (or null for general lot) */
  selectionOnly?: boolean;
  onSelect?: (spotId: string | null) => void;
  /**
   * COMPLETION MODE. Ordinary relocation preselects "General Lot" because a
   * move to the lot is the common, reversible case. Completion is neither:
   * it ends the visit, and CORE-AO makes the lower-precision answer a
   * DELIBERATE choice rather than something a default supplies. In this mode
   * nothing is preselected and Confirm stays disabled until a human picks.
   */
  requireExplicitSelection?: boolean;
}

export const ParkingSpotPickerDialog = ({
  isOpen,
  onClose,
  ticketId,
  shopId,
  onComplete,
  selectionOnly = false,
  onSelect,
  requireExplicitSelection = false,
}: ParkingSpotPickerDialogProps) => {
  const { toast } = useToast();
  const { shopTimezone } = useShopTimezone();
  const [spots, setSpots] = useState<any[]>([]);
  // '' means NOT ASKED YET, and is only reachable in completion mode.
  const [selectedSpotId, setSelectedSpotId] = useState<string>(
    requireExplicitSelection ? '' : 'general');
  const [isLoading, setIsLoading] = useState(false);
  const [ticketPickedUpAt, setTicketPickedUpAt] = useState<string | null>(null);

  // IV-D-29 R157-MED-3 / Chronomancer R-LOC-7 (superseded by #15): this used to
  // read occupancy from the legacy ticket columns with a status IN list.
  // #15: which spots are free comes from current physical truth — the same
  // occupancy the move authority checks under its lock — not from the legacy
  // ticket columns. Re-read on open, on a refusal (this view was behind) and
  // when the page comes back to the foreground; latest read wins.
  const readGate = useRef(createLatestReadGate()).current;
  const loadAvailableSpots = useCallback(async () => {
    if (!shopId) return;
    const token = readGate.begin();
    const [{ data: spotRows }, occupied, { data: currentTicket }] = await Promise.all([
      supabase
        .from('parking_lot_spots')
        .select('id, label, spot_type')
        .eq('shop_id', shopId)
        .eq('is_active', true)
        .order('label'),
      readShopOccupiedSpotIds(shopId).catch((e: unknown) => {
        console.error('[ParkingSpotPickerDialog] current spot occupancy unavailable', e);
        return null;
      }),
      // FIX-R34-H-4: explicit shop predicate on top of RLS. picked_up_at
      // drives the return flow; parking_spot_id keeps this car's own spot.
      ticketId
        ? supabase
            .from('tickets')
            .select('parking_spot_id, picked_up_at')
            .eq('id', ticketId)
            .eq('shop_id', shopId)
            .single()
        : Promise.resolve({ data: null }),
    ]);
    if (!readGate.commit(token)) return;

    const currentSpotId = (currentTicket as any)?.parking_spot_id ?? null;
    setTicketPickedUpAt((currentTicket as any)?.picked_up_at || null);

    // Unknown occupancy offers no specific spot: "probably free" is not
    // something to show. The general lot stays available.
    const available = occupied === null ? [] : (spotRows || [])
      .map((s: any) => ({ id: s.id, label: s.label, spot_type: s.spot_type || 'Standard' }))
      .filter((s) => !occupied.has(s.id) || occupied.get(s.id) === ticketId || s.id === currentSpotId);

    available.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    setSpots(available);
    // A spot that has just been taken can no longer stay chosen.
    setSelectedSpotId((prev) => (prev === '' || prev === 'general' || available.some((s) => s.id === prev)
      ? prev
      : (requireExplicitSelection ? '' : 'general')));
  }, [shopId, ticketId, requireExplicitSelection]);

  useEffect(() => {
    if (!isOpen || !shopId) return;
    void loadAvailableSpots();
    // Re-armed on every open, and per ticket: a location chosen for one
    // vehicle must never be carried into the next one.
    setSelectedSpotId(requireExplicitSelection ? '' : 'general');
  }, [isOpen, shopId, ticketId, requireExplicitSelection]);

  useForegroundRecovery(() => { void loadAvailableSpots(); }, { enabled: isOpen && !!shopId });

  const handleConfirm = async () => {
    // Belt and braces: the button is disabled, but an unanswered question
    // must never leave here looking like "general lot".
    if (requireExplicitSelection && selectedSpotId === '') return;
    const spotId = selectedSpotId === 'general' ? null : selectedSpotId;

    if (selectionOnly) {
      onSelect?.(spotId);
      onClose();
      return;
    }

    if (!ticketId) return;

    setIsLoading(true);
    try {
      const { data: rawData, error } = await supabase.rpc('move_vehicle_location', {
        p_ticket_id: ticketId,
        p_target_parking_spot_id: spotId,
      });

      if (error) throw error;

      const result = rawData as unknown as { success: boolean; reason?: string };
      if (result && !result.success) {
        toast({
          title: 'Cannot move to spot',
          description: result.reason || 'Spot unavailable',
          variant: 'destructive',
        });
        // #15: refused ("already occupied") means this list was behind.
        void loadAvailableSpots();
        return;
      }

      // Codex: Picked-up-incomplete return flow
      // If this ticket had picked_up_at set, clear it, reactivate, cancel stale reminders, cascade.
      // Surface failure: a unique_violation here is rare but possible if another
      // caller raced to claim this spot between the RPC succeeding above and this
      // reactivation update — without checking the result the user would see a
      // "Vehicle returned" toast even though the ticket is still flagged as picked-up.
      if (ticketPickedUpAt) {
        // V-A-34: routed through mutateTicketSchedule. status='Active'
        // is the schedule-allowlisted field (added in V-A-26);
        // picked_up_at and reactivate_at are companion metadata for
        // the customer-return event. skipCascade:true preserves the
        // bespoke triggerBoardCascade below as the sole authoritative
        // cascade for this return flow.
        //
        // Surface failure: a unique_violation here is rare but possible
        // if another caller raced to claim this spot between the RPC
        // succeeding above and this reactivation update — without
        // checking the result the user would see a "Vehicle returned"
        // toast even though the ticket is still flagged as picked-up.
        const result = await mutateTicketSchedule(
          ticketId,
          { status: 'Active' },
          {
            skipCascade: true,
            reason: 'parking_spot_picker_picked_up_return',
            companionFields: {
              picked_up_at: null,
              reactivate_at: null,
            },
          },
        );

        if (!result.success) {
          toast({
            title: 'Vehicle parked, but reactivation failed',
            description: result.error || 'Could not clear picked_up_at. Please retry from the ticket menu.',
            variant: 'destructive',
          });
          // Still close the dialog — the spot move did succeed. Caller's
          // onComplete refresh will show the partial state for follow-up.
          onComplete?.();
          onClose();
          return;
        }

        // OCIS-R69-FIX (HIGH-4): picked-up-return reactivation previously
        // wrote no timeline event. The ticket silently flipped back to
        // Active after the customer returned the vehicle — with no audit
        // trail for the return event. Emit ticket_status_changed so the
        // timeline records the customer return.
        const { data: { user } } = await supabase.auth.getUser();
        logTicketEventToOcis({
          ticketId,
          shopId,
          eventType: 'ticket_status_changed',
          content: 'Vehicle returned after pickup — ticket reactivated',
          authorUserId: user?.id,
          payload: {
            toStatus: 'Active',
            reason: 'picked_up_return',
            parking_spot_id: spotId,
          },
        }).catch(err => console.error('[ParkingSpotPickerDialog] Return OCIS log error:', err));

        // Cancel stale picked-up reminders
        await Promise.all([
          supabase.functions.invoke('cancel-reminders', {
            body: { ticketId, reminderType: 'picked_up_followup' },
          }),
          supabase.functions.invoke('cancel-reminders', {
            body: { ticketId, reminderType: 'escalation' },
          }),
        ]);

        // Trigger cascade for the returning vehicle
        const todayStr = getTodayInShopTz(shopTimezone);
        triggerBoardCascade(shopId, todayStr).catch(err =>
          console.warn('[ParkingSpotPickerDialog] Return-flow cascade error:', err)
        );
      }

      const spotLabel = spotId
        ? spots.find(s => s.id === spotId)?.label
        : null;

      toast({
        title: ticketPickedUpAt ? 'Vehicle returned' : 'Moved to parking',
        description: spotLabel ? `Assigned to ${spotLabel}` : 'Moved to general lot',
      });

      onComplete?.();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to move vehicle',
        variant: 'destructive',
      });
      void loadAvailableSpots();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Select Parking Spot
          </DialogTitle>
          <DialogDescription>
            {requireExplicitSelection
              ? 'Where did the vehicle go? This is recorded as part of finishing the work.'
              : 'Choose a specific parking spot or use the general lot.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Select value={selectedSpotId} onValueChange={setSelectedSpotId}>
            <SelectTrigger>
              <SelectValue placeholder={requireExplicitSelection
                ? 'Choose where the vehicle is…'
                : 'Select a spot...'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General Lot (no specific spot)</SelectItem>
              {spots.map(spot => (
                <SelectItem key={spot.id} value={spot.id}>
                  {spot.label} — {spot.spot_type || 'Standard'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || (requireExplicitSelection && selectedSpotId === '')}
          >
            {isLoading ? 'Moving...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
